import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  CheckCircle2,
  Loader2,
  Save,
  Eye,
  EyeOff,
  Trash2,
  ShieldCheck,
  Film,
  AlertTriangle,
} from "lucide-react";
import YoutubeConnectionCard from "@/components/YoutubeConnectionCard";
import CreditsPanel from "@/components/CreditsPanel";

function PipelineSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = () =>
    api.get("/settings/pipeline").then((r) => setSettings(r.data)).catch(() => {
      setSettings({ strict_animation: true });
    });

  useEffect(() => {
    refresh();
  }, []);

  const toggle = async (next) => {
    setSaving(true);
    try {
      const r = await api.put("/settings/pipeline", {
        strict_animation: next,
      });
      setSettings(r.data);
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="flex items-center gap-2 text-stone-500">
        <Loader2 className="h-4 w-4 animate-spin" /> loading…
      </div>
    );
  }

  const on = !!settings.strict_animation;
  return (
    <div data-testid="pipeline-settings" className="space-y-3">
      <div
        className={`flex items-start gap-4 rounded-lg border p-4 ${
          on
            ? "border-emerald-300 bg-emerald-50/60"
            : "border-amber-300 bg-amber-50/60"
        }`}
      >
        <div
          className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
            on ? "bg-emerald-200 text-emerald-800" : "bg-amber-200 text-amber-800"
          }`}
        >
          <Film className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-3">
            <div className="font-semibold text-stone-800">
              🎬 Strict animation mode
            </div>
            <Switch
              data-testid="strict-animation-toggle"
              checked={on}
              disabled={saving}
              onCheckedChange={toggle}
            />
          </div>
          <p className="mt-1 text-xs text-stone-700">
            {on
              ? "Videos will only ship with full AI animation. If providers are temporarily unavailable, videos wait + retry instead of using static fallbacks. Recommended for production-quality output."
              : "⚠ Fallback mode: when fal.ai or Suno fail, videos ship with Ken Burns panning stills / silent tracks. Use only during major provider outages."}
          </p>
          {!on ? (
            <div className="mt-2 flex items-center gap-1 text-xs text-amber-800">
              <AlertTriangle className="h-3 w-3" />
              Existing videos generated in fallback mode show the
              "Has Fallbacks" badge in the Library.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ProviderRow({ provider, onSaved }) {
  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const save = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      await api.post("/settings/credentials", {
        provider: provider.key,
        value,
      });
      setValue("");
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post(`/settings/test/${provider.key}`);
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, message: e?.response?.data?.detail || e.message });
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove the ${provider.label} credential?`)) return;
    await api.delete(`/settings/credentials/${provider.key}`);
    setTestResult(null);
    onSaved();
  };

  return (
    <div
      data-testid={`provider-${provider.key}`}
      className="rounded-lg border border-orange-100 bg-white/80 p-4 shadow-sm"
    >
      <div className="mb-2 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-stone-800">{provider.label}</div>
          <div className="text-xs text-stone-500">{provider.placeholder}</div>
        </div>
        <Badge
          className={
            provider.configured
              ? "bg-emerald-600 hover:bg-emerald-600"
              : "bg-stone-200 text-stone-700 hover:bg-stone-200"
          }
        >
          {provider.configured ? "configured" : "not set"}
        </Badge>
      </div>
      <div className="flex items-stretch gap-2">
        <div className="relative flex-1">
          <Input
            type={show ? "text" : "password"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={provider.placeholder}
            data-testid={`input-${provider.key}`}
            className="pr-10 border-orange-200"
          />
          <button
            type="button"
            onClick={() => setShow(!show)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
          >
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <Button
          onClick={save}
          disabled={!value.trim() || saving}
          className="bg-gradient-to-r from-orange-500 to-rose-500 text-white"
          data-testid={`save-${provider.key}`}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        </Button>
        <Button
          onClick={test}
          variant="outline"
          disabled={!provider.configured || testing}
          data-testid={`test-${provider.key}`}
          className="border-orange-300"
        >
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Test"}
        </Button>
        {provider.configured ? (
          <Button
            onClick={remove}
            variant="ghost"
            size="icon"
            data-testid={`delete-${provider.key}`}
            className="text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
      {testResult ? (
        <div
          data-testid={`test-result-${provider.key}`}
          className={`mt-2 rounded-md px-3 py-2 text-xs ${
            testResult.ok
              ? "bg-emerald-50 text-emerald-700"
              : "bg-red-50 text-red-700"
          }`}
        >
          {testResult.message}
        </div>
      ) : null}
      {provider.last_updated ? (
        <div className="mt-1 text-xs text-stone-400">
          Last updated: {new Date(provider.last_updated).toLocaleString()}
        </div>
      ) : null}
    </div>
  );
}

function BrandingForm() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    api.get("/settings/branding").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/branding", {
        channel_name: form.channel_name,
        primary_color: form.primary_color,
        outro_cta_text: form.outro_cta_text,
        intro_bumper_enabled: !!form.intro_bumper_enabled,
        quality_footer_enabled:
          form.quality_footer_enabled === undefined
            ? null
            : !!form.quality_footer_enabled,
        burn_captions_into_mp4: !!form.burn_captions_into_mp4,
      });
      setForm(r.data);
      setOk(true);
      setTimeout(() => setOk(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <div className="flex items-center gap-2 text-stone-500">
        <Loader2 className="h-4 w-4 animate-spin" /> loading…
      </div>
    );
  }

  return (
    <div data-testid="branding-form" className="space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium text-stone-700">
          Channel name
        </label>
        <Input
          data-testid="branding-channel-name"
          value={form.channel_name}
          onChange={(e) => update({ channel_name: e.target.value })}
          className="border-orange-200"
        />
      </div>
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label className="mb-1 block text-sm font-medium text-stone-700">
            Primary color
          </label>
          <Input
            data-testid="branding-primary-color"
            value={form.primary_color}
            onChange={(e) => update({ primary_color: e.target.value })}
            className="border-orange-200"
            placeholder="#FF7A59"
          />
        </div>
        <div
          className="h-10 w-16 rounded-md border border-orange-200"
          style={{ background: form.primary_color }}
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-stone-700">
          Outro CTA text
        </label>
        <Input
          data-testid="branding-outro-cta"
          value={form.outro_cta_text}
          onChange={(e) => update({ outro_cta_text: e.target.value })}
          className="border-orange-200"
        />
      </div>
      <div className="flex items-center justify-between rounded-md border border-orange-100 bg-white/60 px-3 py-2">
        <div>
          <div className="text-sm font-medium text-stone-700">Intro bumper</div>
          <div className="text-xs text-stone-500">
            Play a short branded intro at the start of each video.
          </div>
        </div>
        <Switch
          data-testid="branding-intro-toggle"
          checked={!!form.intro_bumper_enabled}
          onCheckedChange={(v) => update({ intro_bumper_enabled: v })}
        />
      </div>
      <div className="flex items-center justify-between rounded-md border border-orange-100 bg-white/60 px-3 py-2">
        <div>
          <div className="text-sm font-medium text-stone-700">
            Quality footer
          </div>
          <div className="text-xs text-stone-500">
            Append "🎬 100% AI-Animated · No Static Slideshows" to YouTube
            descriptions. Auto-suppressed when 30d animation quality &lt; 95%
            so the claim is always true.
          </div>
        </div>
        <Switch
          data-testid="branding-quality-footer-toggle"
          checked={form.quality_footer_enabled !== false}
          onCheckedChange={(v) => update({ quality_footer_enabled: v })}
        />
      </div>
      <div className="flex items-center justify-between rounded-md border border-orange-100 bg-white/60 px-3 py-2">
        <div>
          <div className="text-sm font-medium text-stone-700">
            Burn captions into MP4 (legacy)
          </div>
          <div className="text-xs text-stone-500">
            Default OFF — captions ship as a YouTube CC track via .srt
            sidecar (cleaner visuals, accessibility, lyrics-in-search).
            Turn ON to also burn lyrics into the MP4 itself.
          </div>
        </div>
        <Switch
          data-testid="branding-burn-captions-toggle"
          checked={!!form.burn_captions_into_mp4}
          onCheckedChange={(v) => update({ burn_captions_into_mp4: v })}
        />
      </div>
      <Button
        onClick={save}
        disabled={saving}
        data-testid="save-branding"
        className="bg-gradient-to-r from-orange-500 to-rose-500 text-white"
      >
        {saving ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Save className="mr-2 h-4 w-4" />
        )}
        Save branding
      </Button>
      {ok ? (
        <span className="ml-2 inline-flex items-center text-xs text-emerald-700">
          <CheckCircle2 className="mr-1 h-3 w-3" /> Saved
        </span>
      ) : null}
    </div>
  );
}

export default function Settings() {
  const [status, setStatus] = useState(null);
  const refresh = () =>
    api.get("/settings/status").then((r) => setStatus(r.data)).catch(() => {});

  useEffect(() => {
    refresh();
  }, []);

  if (!status) {
    return (
      <div className="flex justify-center py-20 text-stone-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="page-settings" className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-stone-800">Settings</h1>
        <p className="text-sm text-stone-600">
          API keys, channel branding, and studio preferences.
        </p>
      </div>

      {/* Universal key banner */}
      <div
        data-testid="universal-key-banner"
        className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-sm ${
          status.emergent_llm_key_active
            ? "border-emerald-300 bg-emerald-50 text-emerald-800"
            : "border-stone-200 bg-stone-50 text-stone-700"
        }`}
      >
        <ShieldCheck className="h-5 w-5 shrink-0" />
        <div className="flex-1">
          <div className="font-semibold">
            Universal LLM Key:{" "}
            {status.emergent_llm_key_active ? "active" : "not active"}
          </div>
          <div className="text-xs">
            {status.emergent_llm_key_active
              ? "We'll use the Emergent LLM Key for all OpenAI / Anthropic / Gemini calls. Your own OpenAI key is used as a fallback if you paste one below."
              : "Paste an OpenAI key below (or add EMERGENT_LLM_KEY to backend/.env to use the universal key)."}
          </div>
        </div>
      </div>

      <Card
        id="credits"
        className="border-orange-200 bg-white/80"
        data-testid="settings-credits"
      >
        <CardHeader>
          <CardTitle>API Credits & Health</CardTitle>
          <CardDescription>
            Live balance / quota / runway for every paid provider Dom Studio
            uses. Auto-refresh every 60s.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CreditsPanel />
        </CardContent>
      </Card>

      <Card className="border-orange-200 bg-white/80">
        <CardHeader>
          <CardTitle>Pipeline</CardTitle>
          <CardDescription>
            Output quality bar for every video Dom Studio generates.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PipelineSettingsCard />
        </CardContent>
      </Card>

      <Card className="border-orange-200 bg-white/80">
        <CardHeader>
          <CardTitle>API Keys</CardTitle>
          <CardDescription>
            Keys are encrypted at rest and never returned from the backend.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {status.providers.map((p) => (
            <ProviderRow key={p.key} provider={p} onSaved={refresh} />
          ))}
        </CardContent>
      </Card>

      <Card className="border-orange-200 bg-white/80">
        <CardHeader>
          <CardTitle>Channel branding</CardTitle>
          <CardDescription>
            Used in titles, outros, and generated videos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <BrandingForm />
        </CardContent>
      </Card>

      <YoutubeConnectionCard />

      <Card className="border-orange-200 bg-white/80">
        <CardHeader>
          <CardTitle>About</CardTitle>
          <CardDescription>
            Dom Studio · Single-user kids content tool
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-stone-700">
          <p>
            Phase 1 adds a real nano-banana character designer. Script, voice
            and scene imaging are still MOCKED and will become live providers in
            Phase 2.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
